import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';

const DirectRequestsList = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [managers, setManagers] = useState([]);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editForm, setEditForm] = useState({
    status: '',
    priority: '',
    assigned_to: ''
  });
  const [updateLoading, setUpdateLoading] = useState(false);

  useEffect(() => {
    fetchRequests();
    fetchManagers();
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetchWithAuth(`/api/direct-requests?${params}`);
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      } else {
        setError('Failed to load requests');
      }
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await fetchWithAuth('/api/employees');
      if (response.ok) {
        const data = await response.json();
        const managersWithAccounts = data.filter(emp => emp.user_account_id);
        setManagers(managersWithAccounts);
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
    }
  };

  const handleEditClick = (request) => {
    setSelectedRequest(request);
    setEditForm({
      status: request.status,
      priority: request.priority,
      assigned_to: request.assigned_to || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;

    setUpdateLoading(true);
    try {
      const response = await fetchWithAuth(`/api/direct-requests/${selectedRequest.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        setEditDialogOpen(false);
        fetchRequests();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update request');
      }
    } catch (err) {
      console.error('Error updating request:', err);
      setError('Failed to update request');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleQuickStatusChange = async (requestId, newStatus) => {
    try {
      const response = await fetchWithAuth(`/api/direct-requests/${requestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        fetchRequests();
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'in_progress': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'default';
      case 'normal': return 'primary';
      case 'high': return 'warning';
      case 'urgent': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRequestTypeLabel = (type) => {
    switch (type) {
      case 'task': return 'Task';
      case 'document': return 'Document';
      case 'meeting': return 'Meeting';
      case 'other': return 'Other';
      default: return type;
    }
  };

  const statuses = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  if (loading && requests.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon /> Direct Requests
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                {statuses.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="body2" color="text.secondary">
              Showing {requests.length} request{requests.length !== 1 ? 's' : ''}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Requests List */}
      {requests.length === 0 ? (
        <Paper sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No requests found
            </Typography>
            <Typography color="text.secondary">
              {statusFilter ? 'Try changing the filter' : 'Requests will appear here when created'}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon color="primary" /> What are Direct Requests?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Direct Requests allow staff to create tasks, document requests, or meeting requests
              for a specific individual. These requests can be assigned to managers and tracked through completion.
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              How to create a request:
            </Typography>
            <Box component="ol" sx={{ pl: 2, '& li': { mb: 1 } }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Navigate to <strong>Individuals</strong> and select a person
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Click the <strong>"Request"</strong> button on their profile
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Fill out the request type, subject, and details
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Optionally assign it to a manager
              </Typography>
            </Box>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Request Types:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip label="Task" size="small" variant="outlined" />
              <Chip label="Document Request" size="small" variant="outlined" />
              <Chip label="Meeting Request" size="small" variant="outlined" />
              <Chip label="Other" size="small" variant="outlined" />
            </Box>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Priority Levels:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              <Chip label="Low" size="small" color="default" />
              <Chip label="Normal" size="small" color="primary" />
              <Chip label="High" size="small" color="warning" />
              <Chip label="Urgent" size="small" color="error" />
            </Box>
          </Box>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {requests.map(request => (
            <Grid item xs={12} md={6} lg={4} key={request.id}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Chip
                        label={request.status.replace('_', ' ')}
                        size="small"
                        color={getStatusColor(request.status)}
                        sx={{ textTransform: 'capitalize', mb: 1 }}
                      />
                      <Chip
                        label={request.priority}
                        size="small"
                        color={getPriorityColor(request.priority)}
                        variant="outlined"
                        sx={{ ml: 1, textTransform: 'capitalize' }}
                      />
                    </Box>
                    <Tooltip title="Edit Request">
                      <IconButton size="small" onClick={() => handleEditClick(request)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Typography variant="h6" gutterBottom noWrap>
                    {request.subject}
                  </Typography>

                  <Chip
                    label={getRequestTypeLabel(request.request_type)}
                    size="small"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />

                  {request.message && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {request.message}
                    </Typography>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {request.individual_name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(request.created_at)}
                    </Typography>
                  </Box>

                  {request.assigned_to_username && (
                    <Typography variant="body2" color="text.secondary">
                      Assigned to: <strong>{request.assigned_to_username}</strong>
                    </Typography>
                  )}

                  {/* Quick actions */}
                  {request.status !== 'completed' && request.status !== 'cancelled' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      {request.status === 'pending' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => handleQuickStatusChange(request.id, 'in_progress')}
                        >
                          Start
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        onClick={() => handleQuickStatusChange(request.id, 'completed')}
                      >
                        Complete
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => handleQuickStatusChange(request.id, 'cancelled')}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Edit Request</Typography>
            <IconButton onClick={() => setEditDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Subject:</strong> {selectedRequest.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Individual:</strong> {selectedRequest.individual_name}
              </Typography>

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={editForm.priority}
                  onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                  label="Priority"
                >
                  {priorities.map(p => (
                    <MenuItem key={p.value} value={p.value}>
                      <Chip size="small" label={p.label} color={getPriorityColor(p.value)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Assign To</InputLabel>
                <Select
                  value={editForm.assigned_to}
                  onChange={(e) => setEditForm({ ...editForm, assigned_to: e.target.value })}
                  label="Assign To"
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {managers.map(manager => (
                    <MenuItem key={manager.user_account_id} value={manager.user_account_id}>
                      {manager.name} {manager.position && `(${manager.position})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updateLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateRequest}
            disabled={updateLoading}
          >
            {updateLoading ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DirectRequestsList;
